import { h } from '@stencil/core';
import { getSvg } from '../assets/svg/svg';
import { backgrounds } from './backgrounds';
import {
  getAssetSrc,
  getTenant,
  getYoutubeVideoId,
  renderResponsiveImages,
} from './helpers';
import type {
  CategoryTeaser,
  CssClassMap,
  ImageParams,
  MediaTypeController,
  ResponsiveImages,
  StaticTeasers,
} from './utils/interfaces';

class RenderUtils {
  youtubeImageBaseUrl = 'https://i3.ytimg.com/';
  videoPoster = 'video-poster-webp-750x420.webp';

  private imageOptions: ImageParams = {
    responsiveImages: null,
    imageSizes: null,
    altText: null,
    imageClass: null,
    imageStyles: null,
    lazyLoad: true,
  };

  private teaserBottomClassMap(): CssClassMap {
    return {
      'teaser-bottom': true,
    };
  }

  private ctaContainerClassMap(value: string): CssClassMap {
    return {
      'teaser-cta-container': true,
      [value]: value !== undefined,
    };
  }

  private mediaWrapperClassMap(value: string): CssClassMap {
    return {
      'media-wrapper': true,
      [value]: value !== undefined,
    };
  }

  private teaserContentClassMap(): CssClassMap {
    return {
      'teaser-content-inner': true,
    };
  }

  openUrl = (url: string) => {
    if (url !== undefined) {
      const win = window.open(url, '_self');
      if (win != null) {
        win.focus();
      }
    }
  };

  shortenLabel = (name: string, trimLength: number) => {
    return name.length > trimLength
      ? `${name.substring(0, trimLength)} ...`
      : name;
  };

  getTeaserHtml = (staticTeaser: StaticTeasers): string | undefined => {
    if (staticTeaser) {
      return this.renderStaticTeaser(staticTeaser);
    }
    return undefined;
  };

  renderCategoryTeaser = ({
    alt,
    image,
    label,
    detailsUrl,
    badge,
  }: CategoryTeaser) => {
    const shortenedLabel = this.shortenLabel(label, 60);
    return (
      <li class={'teaser-content-inner category'}>
        <a
          tabIndex={-1}
          style={{ display: 'block', textDecoration: 'none' }}
          title={alt}
          href={detailsUrl}
          class={'teaser-href'}
        >
          <div class={'category-image-wrapper'}>
            <div class={'badge-wrapper'}>
              {badge !== null ? (
                <cms-badge name={badge} theme='primary' size='badge-sm' />
              ) : null}
            </div>

            <img
              style={{ width: '100%', height: 'auto' }}
              loading='lazy'
              width='500'
              height='400'
              src={image}
              alt={alt}
              title={alt}
              class='teaser-image category-image'
            />
          </div>

          <cms-link
            class='target-name'
            href=''
            type='standalone-inverted'
            linkTitle={shortenedLabel}
            content={shortenedLabel}
            size='lg'
            spacing='none'
            icon='arrowShaftRight'
            tag='span'
            style={{
              '--cms-text-color':
                'var(--cms-semanticColors-base-medium-iconOnBackground)',
            }}
          />
        </a>
      </li>
    );
  };

  generateYoutubeImages(videoId: string): ResponsiveImages {
    if (!videoId) {
      return {} as ResponsiveImages;
    }

    // Cleanup ID (Remove additional params like ?rel and ?list)
    const cleanId = getYoutubeVideoId(videoId);

    const youtubeImages: ResponsiveImages = {
      bp359: `${this.youtubeImageBaseUrl}vi_webp/${cleanId}/mqdefault.webp`,
      bp480: `${this.youtubeImageBaseUrl}vi_webp/${cleanId}/hqdefault.webp`,
      bp768: `${this.youtubeImageBaseUrl}vi_webp/${cleanId}/sddefault.webp`,
      bp1280: `${this.youtubeImageBaseUrl}vi_webp/${cleanId}/sddefault.webp`,
      jpgDefault: `${this.youtubeImageBaseUrl}vi/${cleanId}/sddefault.jpg`,
    };

    return youtubeImages;
  }

  renderYoutubeImages(imageParams: ImageParams, videoAlt?: string) {
    const wrapperStyles = {
      position: 'relative',
      height: '100%',
    };
    const youtubeImageStyles = {
      display: 'block',
      width: '100%',
      height: '100%',
      aspectRatio: '1.5 / 1',
      backgroundColor: '#000',
      objectFit: 'cover',
    };
    const iconWrapperStyles = {
      maxWidth: '25%',
      width: '25%',
      height: 'auto',
      position: 'var(--cms-play-icon-position)',
      transform: 'var(--cms-play-icon-transform)',
      top: 'var(--cms-play-icon-top)',
      left: 'var(--cms-play-icon-left)',
      opacity: 'var(--cms-play-icon-opacity)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    };

    return (
      <div style={wrapperStyles} class={'youtube-image-wrapper'}>
        <picture class={'image-wrapper'} style={youtubeImageStyles}>
          <source
            type='image/webp'
            sizes={imageParams.imageSizes}
            srcSet={`${imageParams.responsiveImages.bp359} 359w,
          ${imageParams.responsiveImages.bp480} 480w,
         ${imageParams.responsiveImages.bp768} 768w, 
        ${imageParams.responsiveImages.bp1280} 1280w`}
          />
          <img
            loading={imageParams.lazyLoad ? 'lazy' : null}
            style={
              imageParams.imageStyles
                ? imageParams.imageStyles
                : youtubeImageStyles
            }
            alt={imageParams.altText}
            title={imageParams.altText}
            class={imageParams.imageClass}
            src={imageParams.responsiveImages.jpgDefault}
            width='768'
            height='512'
          />
        </picture>
        <div style={iconWrapperStyles} class={'play-icon-wrapper'}>
          <cms-icon size='xxl' icon='youtubePlay' alt={videoAlt} />
        </div>
      </div>
    );
  }

  renderVideoImage = (altText: string): HTMLElement => {
    const iconWrapperStyles = {
      maxWidth: '25%',
      width: '25%',
      height: 'auto',
      position: 'var(--cms-play-icon-position)',
      transform: 'var(--cms-play-icon-transform)',
      top: 'var(--cms-play-icon-top)',
      left: 'var(--cms-play-icon-left)',
      opacity: 'var(--cms-play-icon-opacity)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    };
    const posterWrapperStyles = {
      position: 'relative',
      height: 'auto',
    };
    const posterStyles = {
      display: 'block',
      width: '100%',
      height: 'auto',
      aspectRatio: '1.5 / 1',
    };

    return (
      <div class={'poster-wrapper'} style={posterWrapperStyles}>
        <img
          style={posterStyles}
          loading='lazy'
          alt={altText}
          title={altText}
          class='video-poster'
          src={getAssetSrc(this.videoPoster)}
          width='750'
          height='420'
        />
        <div class={'play-icon-wrapper'} style={iconWrapperStyles}>
          <cms-icon
            size='xxl'
            icon='controlPlay'
            color-token='--cms-semanticColors-base-inverted-strong-icon'
          />
        </div>
      </div>
    );
  };

  private mergeMediaControllerProps(
    mediaController: MediaTypeController,

    props: object,
  ) {
    return {
      ...mediaController,
      ...props,
    };
  }

  renderStaticTeaser = ({
    altText,
    badge,
    images,
    imageSizes,
    type,
    targetPageName,
    youTubeId,
    background,
    imageClassName,
    target,
    targetFrame,
    accessibleName,
  }: StaticTeasers) => {
    const mediaTypeControllerProps = {
      imageSizes,
      altText,
      className: imageClassName || 'teaser-image',
      youTubeId,
    };

    if (youTubeId) {
      this.mediaTypeController = this.mergeMediaControllerProps(
        this.mediaTypeController,
        { images: this.generateYoutubeImages(youTubeId) },
      );
    } else if (images !== undefined) {
      this.mediaTypeController = this.mergeMediaControllerProps(
        this.mediaTypeController,
        { images },
      );
    }

    this.mediaTypeController = this.mergeMediaControllerProps(
      this.mediaTypeController,
      mediaTypeControllerProps,
    );

    const { mediaType } = this.mediaTypeController;

    const renderMedia = mediaType[type];
    const scratchStyles = {
      minHeight: '3px',
      height: '5px',
      display: 'flex',
      position: 'absolute',
      bottom: '0',
      zIndex: '100',
      width: '100%',
    };
    const anchorStyles = {
      color: 'transparent',
      textDecoration: 'none',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      flexWrap: 'nowrap',
      verticalAlign: 'middle',
    };
    const ctaContainerStyles = {
      height: 'var(--cms-container-height, 100%)',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      justifyContent: ' flex-start',
      backgroundColor: 'var(--cms-static-teaser-bottom-bg, )',
    };
    return (
      <li class={this.teaserContentClassMap()}>
        <a
          tabIndex={-1}
          style={anchorStyles}
          title={altText}
          href={target}
          class={'teaser-href'}
          target={targetFrame}
          aria-label={accessibleName ?? null}
        >
          <div
            style={{ position: 'relative', height: '100%' }}
            class={this.mediaWrapperClassMap(background)}
          >
            {getTenant() === 'hornbach' ? (
              <div
                innerHTML={getSvg('cardScratch')}
                style={scratchStyles}
                class={'scratch'}
              />
            ) : null}
            <div class={'badge-wrapper'}>
              {badge !== null ? (
                <cms-badge name={badge} theme='service' size='badge-sm' />
              ) : null}
            </div>

            {renderMedia()}
          </div>
          <div
            style={ctaContainerStyles}
            class={this.ctaContainerClassMap(background)}
          >
            <div class={this.teaserBottomClassMap()}>
              <cms-link
                style={{ '--cms-link-gap': 'var(--cms-spacing-012)' }}
                class='target-name'
                href=''
                type='standalone-inverted'
                linkTitle={this.shortenLabel(targetPageName, 60)}
                content={this.shortenLabel(targetPageName, 60)}
                size='lg'
                spacing='none'
                icon='arrowShaftRight'
                tag='span'
              />
            </div>
          </div>
        </a>
      </li>
    );
  };

  getBgImageSrc = (image: string) => {
    if (!image) {
      return;
    }
    const bgImage = backgrounds[image]?.bg;
    return getAssetSrc(bgImage);
  };

  getBgTheme = (image: string) => {
    if (!image) {
      return;
    }

    return backgrounds[image].theme;
  };

  mediaTypeController: MediaTypeController = {
    altText: null,
    images: null,
    imageSizes: null,
    className: null,
    youTubeId: null,
    mediaType: {
      image: () => {
        return renderResponsiveImages(
          this.mediaTypeController.images,
          this.mediaTypeController.imageSizes,
          this.mediaTypeController.altText,
          this.mediaTypeController.className,
        );
      },
      ytvideo: () => {
        this.imageOptions = {
          ...this.imageOptions,
          responsiveImages: this.mediaTypeController.images,
          imageSizes: this.mediaTypeController.imageSizes,
          altText: this.mediaTypeController.altText,
          imageClass: this.mediaTypeController.className,
        };
        return this.renderYoutubeImages(this.imageOptions);
      },
      video: () => {
        return this.renderVideoImage(this.mediaTypeController.altText);
      },
    },
  };

  renderMobilePagination = (
    separator: string,
    totalSlides: number,
    currentIndex: number,
  ) => {
    return (
      <div class='splide-mobile-pagination'>
        <span class='current-index'>{currentIndex ? currentIndex : 1}</span>
        <span class='of'>{separator ? separator : 'von'}</span>
        <span class='total-slides'>{totalSlides}</span>
      </div>
    );
  };
}

export const renderTools = new RenderUtils();
